import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  computed,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil, filter } from 'rxjs';

import {
  TableColumn,
  PaginationEvent,
  SortEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { PermissionService } from '@app/core/services/permission.service';
import {
  PurchaseReturnOrderItem,
  POR_SEARCH_CONFIG,
  POR_TABLE_COLUMNS,
  POR_DEFAULT_COLUMNS_ORDER,
  POR_FLEXIBLE_COLUMNS_ORDER,
  POR_ACTION_COLUMN,
  POR_STATUS_OPTIONS,
  getPORStatusSeverity,
  getPORStatusLabel,
  getPORStatusStyle,
  formatCurrency,
} from './purchase-return-order.config';
import { PurchaseReturnOrderService } from './purchase-return-order.service';
import { formatDeliveryDate } from '../../stock/delivery/delivery.helpers';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { VendorOptionsService } from '@shared/services/vendor-options.service';

@Component({
  selector: 'app-purchase-return-order',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    MenuModule,
    AdvancedDataTableComponent,
    AppButtonComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './purchase-return-order.component.html',
  styleUrl: './purchase-return-order.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseReturnOrderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private messageService = inject(MessageService);
  private permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private porService = inject(PurchaseReturnOrderService);
  private warehouseOptionsService = inject(WarehouseOptionsService);
  private vendorOptionsService = inject(VendorOptionsService);
  private destroy$ = new Subject<void>();

  // Pagination
  first = 0;
  rows = 20;

  // Configuration
  searchFormConfig = signal(
    withPaginatedSearchFields(POR_SEARCH_CONFIG, {
      vendorCode: {
        loadPage: (params) =>
          this.vendorOptionsService.getOptionsPage(params, { valueKey: 'code' }),
      },
      warehouseCode: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            status: true,
            valueKey: 'code',
          }),
      },
    }),
  );
  statusOptions = POR_STATUS_OPTIONS;

  // Permission flags
  // Using specific submenu 'purchase_return_order' for procurement permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'procurement.purchase_return_order.view',
    );
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'procurement.purchase_return_order.create',
    );
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'procurement.purchase_return_order.edit',
    );
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'procurement.purchase_return_order.delete',
    );
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'procurement.purchase_return_order.approve',
    );
  });

  // Check if user has any action permissions (READ, UPDATE, DELETE, or APPROVE)
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove(),
  );

  // Table configuration - computed to conditionally include actions column
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POR_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of POR_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(POR_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  // Flexible columns (empty for now, can be used for column customization)
  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POR_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const prop of POR_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Primary button configuration
  primaryButton = computed(() => ({
    label: 'common.actions.new',
    severity: 'info' as const,
    show: this.canCreate(),
    icon: 'add',
  }));

  // Data signals
  dataSource = signal<PurchaseReturnOrderItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Sorting signals
  sortField = signal<string | undefined>('creationDate');
  sortOrder = signal<1 | -1>(-1); // Default: descending (newest first)

  // Menu properties
  @ViewChild('actionMenu') actionMenu!: Menu;
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;
  currentMenuModel = signal<any[]>([]);
  selectedRowData: PurchaseReturnOrderItem | null = null;

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedPOR: PurchaseReturnOrderItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'approve' | 'reject' | 'cancel' | null = null;

  // Search parameters
  searchParams = signal<SearchFormData & { page?: number; size?: number }>({
    page: 0,
    size: 20,
  });

  ngOnInit(): void {
    this.loadData();
    this.setupRouterListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load data
  private loadData(): void {
    this.loading.set(true);
    this.porService
      .getPORList({
        ...this.searchParams(),
        page: this.first,
        size: this.rows,
        sortBy: this.sortField(),
        ascending: this.sortOrder() === 1,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.data);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading POR list:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.load_error',
            ),
          });
          this.loading.set(false);
        },
      });
  }

  private setupRouterListener(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        if (
          event.url.includes('/procurement/purchase-return-order') &&
          !event.url.includes('/add') &&
          !event.url.includes('/edit') &&
          !event.url.includes('/view')
        ) {
          this.loadData();
        }
      });
  }

  // Search
  onSearch(searchData: SearchFormData): void {
    this.searchParams.set({ ...this.searchParams(), ...searchData, page: 0 });
    this.first = 0;
    this.loadData();
  }

  // Reset
  onReset(): void {
    this.searchParams.set({ page: 0, size: 20 });
    this.first = 0;
    this.loadData();
  }

  // Pagination
  onPageChange(event: PaginationEvent): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 20;
    this.loadData();
  }

  // Sorting
  onSortChange(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);
    this.first = 0; // Reset to first page when sorting changes
    this.loadData();
  }

  // Actions
  onAdd(): void {
    // Check permission before navigating
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/procurement/purchase-return-order/add']);
  }

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }

  onView(record: PurchaseReturnOrderItem): void {
    // Check permission before navigating
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate([
      '/procurement/purchase-return-order/view',
      record.id,
    ]);
  }

  onEdit(record: PurchaseReturnOrderItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    if (record.status === 'PENDING') {
      this.router.navigate([
        '/procurement/purchase-return-order/edit',
        record.id,
      ]);
    }
  }

  // Cancel RTV with confirmation
  onCancel(record: PurchaseReturnOrderItem): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPOR = record;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'purchase_return_order.messages.confirmation.cancel',
      { porNo: record.porNo || record.rtvNo },
    );
    this.currentAction = 'cancel';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  // Handle confirmation dialog button click
  onConfirmatiButtonClick(action: string): void {
    if (action === 'confirm' && this.selectedPOR) {
      this.executeConfirmedAction();
    }
    this.closeConfirmationDialog();
  }

  // Execute confirmed action
  private executeConfirmedAction(): void {
    if (!this.selectedPOR) return;

    switch (this.currentAction) {
      case 'approve':
        this.confirmApprove();
        break;
      case 'reject':
        this.confirmReject();
        break;
      case 'cancel':
        this.confirmCancel();
        break;
    }
  }

  // Confirm approve POR
  private confirmApprove(): void {
    if (!this.selectedPOR) return;

    // Check permission before confirming
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      this.closeConfirmationDialog();
      return;
    }

    this.porService
      .updatePOR(this.selectedPOR.id, { status: 'APPROVED' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Get porNo from response first, then fallback to selectedPOR
          const porNo = response?.data?.code || this.selectedPOR?.porNo || '';

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.success.approved',
              { porNo },
            ),
          });
          this.loadData();
        },
        error: (error) => {
          console.error('Error approving POR:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.error.approve_failed',
            ),
          });
        },
      });
  }

  // Confirm reject POR (Change status to REJECTED)
  private confirmReject(): void {
    if (!this.selectedPOR) return;

    // Check permission before confirming
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      this.closeConfirmationDialog();
      return;
    }

    this.porService
      .updatePOR(this.selectedPOR.id, { status: 'REJECTED' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Get porNo from response first, then fallback to selectedPOR
          const porNo = response?.data?.code || this.selectedPOR?.porNo || '';

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.success.rejected',
              { porNo },
            ),
          });
          this.loadData();
        },
        error: (error) => {
          console.error('Error rejecting POR:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.error.reject_failed',
            ),
          });
        },
      });
  }

  // Confirm cancel POR (Requires delete permission)
  private confirmCancel(): void {
    if (!this.selectedPOR) return;

    // Check permission before confirming
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      this.closeConfirmationDialog();
      return;
    }

    this.porService
      .cancelPOR(this.selectedPOR.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Get porNo from response first, then fallback to selectedPOR
          const porNo = response?.data?.code || this.selectedPOR?.porNo || '';

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.success.cancelled',
              { porNo },
            ),
          });
          this.loadData();
        },
        error: (error) => {
          console.error('Error cancelling POR:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.error.cancel_failed',
            ),
          });
        },
      });
  }

  // Close confirmation dialog
  private closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedPOR = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Get available actions for POR based on status
  getPORActions(por: PurchaseReturnOrderItem): string[] {
    const status = por.status;
    const actions: string[] = [];

    switch (status) {
      case 'PENDING':
        actions.push('edit', 'approve', 'reject', 'cancel');
        break;
      case 'APPROVED':
        actions.push('create_odo');
        actions.push('cancel');
        break;
      case 'PARTIALLY_SHIPPED':
        actions.push('create_odo');
        break;
      case 'REJECTED':
      case 'IN_PROGRESS':
      case 'FULLY_SHIPPED':
      case 'RECEIVED_BY_VENDOR':
      case 'COMPLETED':
      case 'CANCELLED':
        // No actions
        break;
      default:
        break;
    }

    return actions;
  }

  // Check if POR has any enabled actions (excluding view)
  hasEnabledPORActions(por: PurchaseReturnOrderItem): boolean {
    const actions = this.getPORActions(por);
    return actions.some((action) => {
      if (action === 'view') return false; // View is handled separately
      if (action === 'edit') return this.canUpdate();
      if (action === 'cancel') return this.canDelete();
      if (action === 'approve') return this.canApprove();
      if (action === 'reject') return this.canApprove();
      if (action === 'create_odo') return this.canUpdate();
      return true;
    });
  }

  // Toggle menu for actions
  toggleMenu(event: Event, rowData: PurchaseReturnOrderItem): void {
    this.selectedRowData = rowData;
    const actions = this.getPORActions(rowData);

    // Create menu items based on available actions with permission checks
    const menuItems = actions
      .filter((action) => {
        if (action === 'view') {
          return this.canRead();
        } else if (action === 'edit') {
          return this.canUpdate();
        } else if (action === 'approve') {
          return this.canApprove();
        } else if (action === 'reject') {
          return this.canApprove();
        } else if (action === 'cancel') {
          return this.canDelete();
        } else if (action === 'create_odo') {
          return this.canUpdate();
        }
        return true;
      })
      .map((action) => {
        // create_odo is in purchase_return_order.actions, not common.actions
        const translationKey =
          action === 'create_odo'
            ? 'purchase_return_order.actions.create_odo'
            : `common.actions.${action}`;
        return {
          label: this.translateService.instant(translationKey),
          command: () => this.onActionClick(action, rowData),
        };
      });

    this.currentMenuModel.set(menuItems);

    // Show menu at cursor position
    this.actionMenu.toggle(event);
  }

  // Handle action clicks
  onActionClick(action: string, rowData: PurchaseReturnOrderItem): void {
    switch (action) {
      case 'view':
        this.onView(rowData);
        break;
      case 'edit':
        this.onEdit(rowData);
        break;
      case 'approve':
        this.onApprove(rowData);
        break;
      case 'reject':
        this.onReject(rowData);
        break;
      case 'cancel':
        this.onCancel(rowData);
        break;
      case 'create_odo':
        this.onCreateODO(rowData);
        break;
    }
  }

  // Approve POR with confirmation
  onApprove(por: PurchaseReturnOrderItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPOR = por;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'purchase_return_order.messages.confirmation.approve',
      { porNo: por.porNo || por.rtvNo },
    );
    this.currentAction = 'approve';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.approve'),
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  // Reject POR with confirmation (Change status, requires approve permission)
  onReject(por: PurchaseReturnOrderItem): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedPOR = por;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'purchase_return_order.messages.confirmation.reject',
      { porNo: por.porNo || por.rtvNo },
    );
    this.currentAction = 'reject';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.reject'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  // Create ODO from POR
  onCreateODO(por: PurchaseReturnOrderItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    // Navigate to stock delivery order add page with POR ID
    this.router.navigate(['/stock/delivery/delivery-order/add'], {
      queryParams: {
        porId: por.id,
        porCode: por.porNo,
        outType: 'purchase-order-return-out-stock',
      },
    });
  }

  // Status helpers
  getStatusSeverity = getPORStatusSeverity;
  getStatusStyle = getPORStatusStyle;
  formatCurrency = formatCurrency;

  // Get status display text (translated)
  getStatusLabel(status: string): string {
    if (!status) {
      return '-';
    }
    const result = getPORStatusLabel(status, this.translateService);
    return result;
  }

  // Date formatting - use formatDeliveryDate like delivery
  formatDate = formatDeliveryDate;

}
